import { useState, useMemo } from "react";
import { Card, Collapse, Typography, Space, Tag, Divider, Row, Col, Statistic, Progress, Timeline, Badge, Tooltip, Button } from "antd";
import { RobotOutlined, CheckCircleOutlined, ExclamationCircleOutlined, InfoCircleOutlined, FileTextOutlined, CalculatorOutlined, EyeOutlined, EyeInvisibleOutlined } from "@ant-design/icons";

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

const AgentAnalysisViewer = ({ agentData, visible = true, onToggleVisibility }) => {
    const [expandedPanels, setExpandedPanels] = useState(["analysis", "operations"]);

    const { reasoning, system_operations, expected_operations_count } = agentData || {};

    // 解析reasoning中的结构化内容
    const parsedReasoning = useMemo(() => {
        if (!reasoning) return null;

        // 尝试解析reasoning中的数字信息
        const lines = reasoning.split("\n");
        const analysisSteps = [];
        let currentSection = null;

        lines.forEach((line) => {
            const trimmed = line.trim();
            if (!trimmed) return;

            // 识别章节标题
            if (trimmed.match(/^\d+\.\s+/)) {
                if (currentSection) {
                    analysisSteps.push(currentSection);
                }
                currentSection = {
                    title: trimmed,
                    content: [],
                };
            } else if (trimmed.match(/^[-*•]/)) {
                const bulletPoint = trimmed.replace(/^[-*•]\s*/, "");
                if (currentSection) {
                    currentSection.content.push(bulletPoint);
                }
            } else if (currentSection) {
                currentSection.content.push(trimmed);
            }
        });

        if (currentSection) {
            analysisSteps.push(currentSection);
        }

        return analysisSteps;
    }, [reasoning]);

    // 解析system_operations中的数据
    const operationStats = useMemo(() => {
        if (!system_operations || !Array.isArray(system_operations)) {
            return { total: 0, create: 0, flag: 0, analysis: 0 };
        }

        return system_operations.reduce(
            (stats, op) => {
                stats.total++;
                switch (op.operation_type) {
                    case "CREATE_INVOICE_RECORDS":
                        stats.create++;
                        break;
                    case "FLAG_FOR_MANUAL_REVIEW":
                        stats.flag++;
                        break;
                    case "ANALYSIS_ONLY":
                        stats.analysis++;
                        break;
                }
                return stats;
            },
            { total: 0, create: 0, flag: 0, analysis: 0 },
        );
    }, [system_operations]);

    // 计算数据完整性指标
    const dataIntegrity = useMemo(() => {
        if (!system_operations || expected_operations_count === undefined) {
            return { score: 0, status: "unknown" };
        }

        const actualCount = system_operations.length;
        const expectedCount = expected_operations_count;
        const score = actualCount === expectedCount ? 100 : Math.max(0, 100 - Math.abs(actualCount - expectedCount) * 10);

        return {
            score,
            status: score === 100 ? "success" : score > 80 ? "warning" : "error",
            actual: actualCount,
            expected: expectedCount,
        };
    }, [system_operations, expected_operations_count]);

    // 提取账单统计信息
    const invoiceStats = useMemo(() => {
        let totalInvoices = 0;
        let totalSections = 0;
        let totalLineItems = 0;
        let totalAnomalies = 0;

        system_operations?.forEach((op) => {
            if (op.operation_type === "CREATE_INVOICE_RECORDS" && op.operation_parameters?.invoice_data) {
                totalInvoices++;
                const invoice = op.operation_parameters.invoice_data;
                totalSections += invoice.sections?.length || 0;

                invoice.sections?.forEach((section) => {
                    totalLineItems += section.line_items?.length || 0;
                    section.line_items?.forEach((item) => {
                        if (item.anomalies?.length > 0) {
                            totalAnomalies++;
                        }
                    });
                });
            }
        });

        return { totalInvoices, totalSections, totalLineItems, totalAnomalies };
    }, [system_operations]);

    if (!agentData || !visible) {
        return null;
    }

    const renderReasoningContent = () => {
        if (!reasoning) {
            return <Text type="secondary">暂无分析说明</Text>;
        }

        return (
            <div style={{ padding: "16px 0" }}>
                {parsedReasoning && parsedReasoning.length > 0 ? (
                    <Timeline>
                        {parsedReasoning.map((step, index) => (
                            <Timeline.Item key={index} color={index === 0 ? "blue" : "gray"} dot={<RobotOutlined />}>
                                <div>
                                    <Title level={5} style={{ margin: "0 0 8px 0", color: "#1890ff" }}>
                                        {step.title}
                                    </Title>
                                    {step.content.length > 0 ? (
                                        <ul style={{ margin: 0, paddingLeft: 16 }}>
                                            {step.content.map((content, idx) => (
                                                <li key={idx} style={{ marginBottom: 4 }}>
                                                    <Text>{content}</Text>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <Text type="secondary">{step.title}</Text>
                                    )}
                                </div>
                            </Timeline.Item>
                        ))}
                    </Timeline>
                ) : (
                    <Paragraph>
                        <pre
                            style={{
                                whiteSpace: "pre-wrap",
                                fontFamily: "inherit",
                                fontSize: "14px",
                                lineHeight: "1.6",
                                margin: 0,
                            }}
                        >
                            {reasoning}
                        </pre>
                    </Paragraph>
                )}
            </div>
        );
    };

    const renderOperationsSummary = () => {
        return (
            <Row gutter={16}>
                <Col span={6}>
                    <Card size="small">
                        <Statistic title="总操作数" value={operationStats.total} prefix={<FileTextOutlined />} valueStyle={{ color: "#1890ff" }} />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card size="small">
                        <Statistic title="创建记录" value={operationStats.create} prefix={<CheckCircleOutlined />} valueStyle={{ color: "#52c41a" }} suffix={operationStats.total > 0 ? `(${Math.round((operationStats.create / operationStats.total) * 100)}%)` : ""} />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card size="small">
                        <Statistic title="需要审核" value={operationStats.flag} prefix={<ExclamationCircleOutlined />} valueStyle={{ color: "#faad14" }} suffix={operationStats.total > 0 ? `(${Math.round((operationStats.flag / operationStats.total) * 100)}%)` : ""} />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card size="small">
                        <Statistic title="仅分析" value={operationStats.analysis} prefix={<InfoCircleOutlined />} valueStyle={{ color: "#722ed1" }} suffix={operationStats.total > 0 ? `(${Math.round((operationStats.analysis / operationStats.total) * 100)}%)` : ""} />
                    </Card>
                </Col>
            </Row>
        );
    };

    const renderDataIntegrity = () => {
        return (
            <Card size="small" title="数据完整性检查">
                <Space direction="vertical" style={{ width: "100%" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Text>操作数量一致性</Text>
                        <Space>
                            <Text type="secondary">
                                实际: {dataIntegrity.actual} / 预期: {dataIntegrity.expected}
                            </Text>
                            <Progress percent={dataIntegrity.score} size="small" status={dataIntegrity.status} showInfo={false} style={{ width: 100 }} />
                        </Space>
                    </div>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Statistic title="账单数量" value={invoiceStats.totalInvoices} prefix={<FileTextOutlined />} />
                        </Col>
                        <Col span={8}>
                            <Statistic title="费用项目" value={invoiceStats.totalLineItems} prefix={<CalculatorOutlined />} />
                        </Col>
                        <Col span={8}>
                            <Statistic title="异常项目" value={invoiceStats.totalAnomalies} prefix={<ExclamationCircleOutlined />} valueStyle={{ color: invoiceStats.totalAnomalies > 0 ? "#ff4d4f" : "#52c41a" }} />
                        </Col>
                    </Row>
                </Space>
            </Card>
        );
    };

    const renderOperationDetails = () => {
        if (!system_operations || system_operations.length === 0) {
            return <Text type="secondary">暂无系统操作</Text>;
        }

        return (
            <div style={{ padding: "16px 0" }}>
                {system_operations.map((operation, index) => (
                    <Card
                        key={operation.operation_id}
                        size="small"
                        style={{ marginBottom: 12 }}
                        title={
                            <Space>
                                <Badge count={index + 1} style={{ backgroundColor: "#52c41a" }} />
                                <Text strong>{operation.operation_type}</Text>
                                <Tag color="blue">{operation.operation_id}</Tag>
                            </Space>
                        }
                    >
                        {operation.operation_type === "CREATE_INVOICE_RECORDS" && operation.operation_parameters?.invoice_data && (
                            <div>
                                <Row gutter={16} style={{ marginBottom: 12 }}>
                                    <Col span={6}>
                                        <Statistic title="账单号" value={operation.operation_parameters.invoice_data.invoice_number} formatter={(value) => value || "-"} />
                                    </Col>
                                    <Col span={6}>
                                        <Statistic title="账单日期" value={operation.operation_parameters.invoice_data.invoice_date} />
                                    </Col>
                                    <Col span={4}>
                                        <Statistic title="币种" value={operation.operation_parameters.invoice_data.currency} />
                                    </Col>
                                    <Col span={4}>
                                        <Statistic title="总金额" value={operation.operation_parameters.invoice_data.expected_grand_total} precision={2} prefix={operation.operation_parameters.invoice_data.currency === "USD" ? "$" : operation.operation_parameters.invoice_data.currency} />
                                    </Col>
                                    <Col span={4}>
                                        <Statistic title="分节数" value={operation.operation_parameters.invoice_data.expected_section_count} />
                                    </Col>
                                </Row>

                                {operation.operation_parameters.invoice_data.sections && (
                                    <Collapse size="small" ghost>
                                        {operation.operation_parameters.invoice_data.sections.map((section, secIndex) => {
                                            const sectionCurrency = section.currency || operation.operation_parameters.invoice_data.currency;
                                            const currencyPrefix = sectionCurrency === "USD" ? "$" : sectionCurrency;
                                            return (
                                                <Panel
                                                    key={secIndex}
                                                    header={
                                                        <Space>
                                                            <Text strong>{section.section_identifier}</Text>
                                                            <Tag color="blue">{section.expected_line_item_count || 0} 项目</Tag>
                                                            <Tag color="green">
                                                                {currencyPrefix} {section.expected_sub_total?.toLocaleString()}
                                                            </Tag>
                                                        </Space>
                                                    }
                                                >
                                                    <div style={{ paddingLeft: 16 }}>
                                                        {section.line_items &&
                                                            section.line_items.map((item, itemIndex) => (
                                                                <div key={itemIndex} style={{ marginBottom: 8, padding: 8, border: "1px solid #f0f0f0", borderRadius: 4 }}>
                                                                    <Row gutter={16}>
                                                                        <Col span={12}>
                                                                            <Text strong>{item.description}</Text>
                                                                        </Col>
                                                                        <Col span={4}>
                                                                            <Text>数量: {item.quantity}{item.unit ? ` ${item.unit}` : ""}</Text>
                                                                        </Col>
                                                                        <Col span={4}>
                                                                            <Text>
                                                                                单价: {currencyPrefix} {item.unit_price?.toFixed(6)}
                                                                            </Text>
                                                                        </Col>
                                                                        <Col span={4}>
                                                                            <Text>
                                                                                总价: {currencyPrefix} {item.total_price?.toLocaleString()}
                                                                            </Text>
                                                                        </Col>
                                                                    </Row>

                                                                    <Row gutter={16} style={{ marginTop: 8 }}>
                                                                        <Col span={6}>
                                                                            <Tag color="blue">{item.cost_type}</Tag>
                                                                        </Col>
                                                                        <Col span={6}>
                                                                            <Tag color="orange">{item.allocation_type}</Tag>
                                                                        </Col>
                                                                        <Col span={6}>{item.matched_flight_id ? <Tag color="green">航班: {item.matched_flight_id}</Tag> : <Tag color="red">未匹配航班</Tag>}</Col>
                                                                        <Col span={6}>
                                                                            {item.anomalies?.length > 0 && (
                                                                                <Tooltip title={item.anomalies.join(", ")}>
                                                                                    <Tag color="red">{item.anomalies.length} 个异常</Tag>
                                                                                </Tooltip>
                                                                            )}
                                                                        </Col>
                                                                    </Row>

                                                                    {item.additional_info && Object.keys(item.additional_info).length > 0 && (
                                                                        <div style={{ marginTop: 8, padding: 8, background: "#fafafa", borderRadius: 4 }}>
                                                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                                                附加信息:
                                                                            </Text>
                                                                            <pre style={{ margin: 0, fontSize: 12, whiteSpace: "pre-wrap" }}>{JSON.stringify(item.additional_info, null, 2)}</pre>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ))}
                                                    </div>
                                                </Panel>
                                            );
                                        })}
                                    </Collapse>
                                )}
                            </div>
                        )}

                        {operation.operation_type === "FLAG_FOR_MANUAL_REVIEW" && (
                            <div>
                                <Text strong>标记原因: </Text>
                                <Text>{operation.operation_parameters?.reason || operation.target_record_selector?.item_id}</Text>
                            </div>
                        )}

                        {operation.operation_type === "ANALYSIS_ONLY" && (
                            <div>
                                <Text type="secondary">仅提供分析，无数据创建操作</Text>
                            </div>
                        )}
                    </Card>
                ))}
            </div>
        );
    };

    return (
        <Card
            title={
                <Space>
                    <RobotOutlined />
                    <span>AI 分析结果</span>
                    <Tag color="blue">版本 4.1</Tag>
                    {dataIntegrity.status === "success" && (
                        <Tag color="green" icon={<CheckCircleOutlined />}>
                            数据完整
                        </Tag>
                    )}
                    {dataIntegrity.status === "warning" && (
                        <Tag color="orange" icon={<ExclamationCircleOutlined />}>
                            数据待验证
                        </Tag>
                    )}
                    {dataIntegrity.status === "error" && (
                        <Tag color="red" icon={<ExclamationCircleOutlined />}>
                            数据异常
                        </Tag>
                    )}
                </Space>
            }
            size="small"
            extra={
                <Button type="text" icon={visible ? <EyeInvisibleOutlined /> : <EyeOutlined />} onClick={onToggleVisibility} size="small">
                    {visible ? "隐藏" : "显示"}
                </Button>
            }
            style={{ marginBottom: 16 }}
        >
            <Collapse activeKey={expandedPanels} onChange={setExpandedPanels} size="small" ghost>
                <Panel
                    header={
                        <Space>
                            <InfoCircleOutlined />
                            <span>AI 分析说明</span>
                            <Badge count={parsedReasoning?.length || 0} style={{ backgroundColor: "#52c41a" }} />
                        </Space>
                    }
                    key="analysis"
                >
                    {renderReasoningContent()}
                </Panel>

                <Panel
                    header={
                        <Space>
                            <CalculatorOutlined />
                            <span>操作概览</span>
                            <Badge count={operationStats.total} style={{ backgroundColor: "#1890ff" }} />
                        </Space>
                    }
                    key="summary"
                >
                    {renderOperationsSummary()}
                    <Divider />
                    {renderDataIntegrity()}
                </Panel>

                <Panel
                    header={
                        <Space>
                            <FileTextOutlined />
                            <span>详细操作</span>
                            {operationStats.total > 0 && <Badge count={operationStats.total} style={{ backgroundColor: "#722ed1" }} />}
                        </Space>
                    }
                    key="operations"
                >
                    {renderOperationDetails()}
                </Panel>
            </Collapse>
        </Card>
    );
};

export default AgentAnalysisViewer;
